/**
 * The lists a step's pickers choose from, loaded by the step that needs them. Each step's
 * editor and its sentence call the same hook, so React Query fetches once and only while a
 * step of that type is on screen. Each list needs its own permission, so each fails alone.
 */
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../../../hooks/useMe.js';
import { apiRequest } from '../../../lib/apiClient.js';
import { queryKeys } from '../../../lib/queryKeys.js';
import type { BotListResponse, GroupListResponse } from '../../../lib/types.js';
import { coversLine, customersQuery, regionsQuery } from '../../reports/reportNarrowing.js';
import { reportTemplatesQuery } from '../../reports/useReportsPageLogic.js';
import type { ReportOption } from '../stepTypes.js';

export const useReportStepOptions = () => {
  const { data: me } = useMe();
  const reports = useQuery({ ...reportTemplatesQuery, retry: false });
  const customers = useQuery({ ...customersQuery, retry: false });
  const regions = useQuery({ ...regionsQuery, retry: false });
  const reportOptions: ReportOption[] = (reports.data ?? []).map((t) => ({
    value: t.id,
    label: t.owner.id === me?.user.id ? t.name : `${t.name} (${t.owner.name ?? 'someone else'})`,
    kind: t.template.units.kind,
    covers: coversLine(t.template.units),
  }));
  return {
    reportOptions,
    reportsError: reports.error ? reports.error.message : null,
    customerOptions: customers.data ?? [],
    regionOptions: regions.data ?? [],
    customersError: customers.error ? 'Customers are unavailable' : null,
    regionsError: regions.error ? 'Regions are unavailable' : null,
  };
};

export const useBotStepOptions = () => {
  const { data: me } = useMe();
  const tenantId = me?.tenant.id ?? '';
  const bots = useQuery({
    queryKey: queryKeys.bots(tenantId),
    queryFn: () => apiRequest<BotListResponse>('/api/bots'),
    enabled: Boolean(me),
    retry: false,
  });
  const groups = useQuery({
    queryKey: queryKeys.groups(tenantId),
    queryFn: () => apiRequest<GroupListResponse>('/api/groups'),
    enabled: Boolean(me),
    retry: false,
  });
  return {
    botOptions: (bots.data?.bots ?? []).map((b) => ({
      value: b.id,
      label: b.enabled ? b.name : `${b.name} (off for everyone)`,
    })),
    groupOptions: (groups.data?.groups ?? []).map((g) => ({
      value: g.id,
      label: `${g.name} · ${String(g.memberCount)} ${g.memberCount === 1 ? 'person' : 'people'}`,
    })),
    botsError: bots.error ? 'Bots are unavailable to you' : null,
    groupsError: groups.error ? 'Groups are unavailable to you' : null,
  };
};
